import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import classes from './jCodeSettings.module.scss';
import api from '../../../../../api/api';
import Utils from '../../../../../utils/utils';
import { useQuery } from '../../../../../utils/hooks/useQuery';
import { getProductsRequest } from '../../../../../store/actions/products';
import Button from '../../../../_common/Form/Buttons/Button';
import InputField from '../../../../_common/Form/TextFields/InputField';
import Select from '../../../../_common/Form/Selects/Select/Select';
import TableAndPagination from '../../../../_common/TableAndPagination/TableAndPagination';
import Loader from '../../../../_common/Loader/Loader';
import JCodeModal from './JCodeModal/JCodeModal';
import { ReactComponent as EditIcon } from '../../../../../assets/icons/edit.svg';
import StatusField from '../../../../_common/StatusField/StatusField';

const statusOptions = ['Active', 'Inactive'];

const medicationTypeOptions = ['Brand', 'Generic', 'Immunotherapy', 'Biosimilars'];

const defaultRequestData = {
  page: 1,
  limit: 15,
  search: '',
  status: '',
  brand: '',
  sortBy: 'name',
  sort: 'asc',
};

// `status` is kept in its display form ('Active') for the select and the url, the api expects it lowercased.
// `otc` is always 0 here and is re-added because deleteEmptyKeys drops it.
const collectRequestData = (requestData) => {
  const data = Utils.deleteEmptyKeys({ ...requestData, status: requestData.status.toLowerCase() });

  data.otc = 0;

  return data;
};

const getTableHeader = (onEditClick) => [
  { name: 'J-Code', path: 'jCode', isSelectable: true },
  { name: 'Medication', path: 'name', isSelectable: true },
  { name: 'NDC', path: 'NDC', isSelectable: true },
  { name: 'Billing Qty', path: 'jQuantityPerFill' },
  { name: 'Billing Unit', path: 'jUnitsPerFill' },
  { name: 'Medication Type', path: 'brand', isSelectable: true },
  {
    name: 'Status',
    render: (d) => <StatusField status={d.active ? 'Active' : 'Inactive'} />,
  },
  {
    name: 'Actions',
    render: (d) => (
      <div className={classes.actions}>
        <button type="button" title="Edit" onClick={() => onEditClick(d)}>
          <EditIcon />
        </button>
      </div>
    ),
  },
];

const JCodeSettings = () => {
  const dispatch = useDispatch();

  const { query, setQuery } = useQuery();

  const { productList, productTotalPage } = useSelector((store) => store.products);

  const [requestData, setRequestData] = useState({
    page: query.page || defaultRequestData.page,
    limit: query.limit || defaultRequestData.limit,
    search: query.search || defaultRequestData.search,
    status: query.status || defaultRequestData.status,
    brand: query.brand || defaultRequestData.brand,
    sortBy: query.sortBy || defaultRequestData.sortBy,
    sort: query.sort || defaultRequestData.sort,
  });

  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [modalLoading, setModalLoading] = useState(false);

  const {
    page, limit, search, status, brand, sort, sortBy,
  } = requestData;

  useEffect(() => {
    (async () => {
      await dispatch(getProductsRequest(collectRequestData(requestData)));

      setLoading(false);
    })();
  }, []);

  const getData = async (newRequestData) => {
    setRequestData(newRequestData);

    setQuery(Utils.deleteEmptyKeys({ ...newRequestData }));

    await dispatch(getProductsRequest(collectRequestData(newRequestData)));
  };

  const tableHeader = useMemo(() => getTableHeader(setModalData), []);

  const changeRequestData = (path, value) => {
    setRequestData((prev) => ({ ...prev, [path]: value }));
  };

  const submitFilters = async (e) => {
    e.preventDefault();

    setTableLoading(true);

    await getData({ ...requestData, page: 1 });

    setTableLoading(false);
  };

  const clearFilters = async () => {
    setTableLoading(true);

    await getData({ ...defaultRequestData, limit });

    setTableLoading(false);
  };

  const onSortBy = async (newSort) => {
    setTableLoading(true);

    await getData({ ...requestData, ...newSort, page: 1 });

    setTableLoading(false);
  };

  const onChangePageData = async (path, count) => {
    const newRequestData = { ...requestData, [path]: count };

    if (path === 'limit') newRequestData.page = 1;

    await getData(newRequestData);
  };

  const submitJCode = async (jCodeData) => {
    setModalLoading(true);

    try {
      const {
        id, jCode, jQuantityPerFill, jUnitsPerFill,
      } = jCodeData;

      await api.editProductJCode({
        id, jCode, jQuantityPerFill, jUnitsPerFill,
      });

      toast.success(modalData?.id ? 'J-Code successfully updated' : 'J-Code successfully created');

      setModalData(null);
      setTableLoading(true);

      await dispatch(getProductsRequest(collectRequestData(requestData)));

      setTableLoading(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Something went wrong');
    }

    setModalLoading(false);
  };

  return (
    <div className={classes.wrapper}>
      <div className={classes.header}>
        <h2>J-Code Settings</h2>

        <div>
          <Button
            className={classes.add_btn}
            title="Add J-Code"
            type="button"
            addButton
            reverseColor
            onClick={() => setModalData({})}
          />
        </div>
      </div>

      <div className={classes.filters}>
        <form className={classes.fields_wrapper} onSubmit={submitFilters}>
          <div className={classes.fields_group}>
            <div className={classes.field_wrapper}>
              <InputField
                label="Search J-code or NDC"
                value={search}
                onChange={(value) => changeRequestData('search', value)}
              />
            </div>

            <div className={classes.field_wrapper}>
              <Select
                label="Status"
                options={statusOptions}
                currentOption={status}
                onChange={(value) => changeRequestData('status', value)}
              />
            </div>

            <div className={classes.field_wrapper}>
              <Select
                label="Medication Type"
                options={medicationTypeOptions}
                currentOption={brand}
                onChange={(value) => changeRequestData('brand', value)}
              />
            </div>
          </div>

          <div className={classes.btns_wrapper}>
            <Button
              title="Search"
              type="submit"
            />

            <Button
              title="Clear"
              type="button"
              clearButton
              onClick={clearFilters}
            />
          </div>
        </form>
      </div>

      <div className={classes.load_wrapper}>
        {loading
          ? <Loader />

          : (
            <TableAndPagination
              data={productList}
              header={tableHeader}
              className={classes.row}
              loading={tableLoading}
              notFoundText="No J-Codes Found"
              totalPage={+productTotalPage}
              page={+page - 1}
              limit={+limit}
              sort={sort}
              sortBy={sortBy}
              onSortBy={onSortBy}
              onChange={onChangePageData}
            />
          )}
      </div>

      <JCodeModal
        isOpen={!!modalData}
        jCode={modalData}
        loading={modalLoading}
        onClose={() => setModalData(null)}
        onSubmit={submitJCode}
      />
    </div>
  );
};

export default JCodeSettings;
